import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Users, Lightbulb, ArrowRight } from 'lucide-react';

const TAGS = [
  { label: 'Network', Icon: Users },
  { label: 'Thoughts', Icon: MessageSquare },
  { label: 'Advice', Icon: Lightbulb },
];

const BUBBLES = [
  {
    side: 'left',
    author: 'Priya Menon',
    role: 'Stylist',
    text: 'Looking for a stylist for an upcoming shoot — DM if you can follow up with leads.',
    tag: 'Network',
    color: 'from-rose-500/25 to-rose-500/5 text-rose-200',
  },
  {
    side: 'right',
    author: 'Arjun Shah',
    role: 'Cinematographer',
    text: 'How do creative freelancers in our industry actually cope with unpredictable work?',
    tag: 'Thoughts',
    color: 'from-sky-500/25 to-sky-500/5 text-sky-200',
  },
  {
    side: 'left',
    author: 'Neha Iyer',
    role: 'Fashion Creator',
    text: 'Any advice on pricing a first-time brand collab? Rates feel like guesswork.',
    tag: 'Advice',
    color: 'from-amber-500/25 to-amber-500/5 text-amber-200',
  },
];

const initialsOf = (name) => name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const Bubble = ({ b, i }) => {
  const isLeft = b.side === 'left';
  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -30 : 30, y: 10 }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`flex items-start gap-3 ${isLeft ? '' : 'flex-row-reverse'}`}
    >
      <div
        className={`h-12 w-12 rounded-full shrink-0 ring-2 ring-zinc-900 flex items-center justify-center font-display font-semibold text-sm bg-gradient-to-br ${b.color}`}
        aria-label={b.author}
      >
        {initialsOf(b.author)}
      </div>
      <div
        className={`relative max-w-md rounded-3xl border border-zinc-800 bg-zinc-900/70 backdrop-blur px-5 py-4 ${
          isLeft ? 'rounded-tl-md' : 'rounded-tr-md'
        }`}
      >
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-zinc-100">{b.author}</span>
          <span className="text-zinc-500">· {b.role}</span>
          <span className="ml-1 rounded-full bg-[#6C63F5]/15 text-[#8b83ff] text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5">
            {b.tag}
          </span>
        </div>
        <p className="mt-2 text-sm md:text-[0.95rem] text-zinc-200 leading-relaxed">{b.text}</p>
      </div>
    </motion.div>
  );
};

export const ConversationSection = () => {
  const navigate = useNavigate();

  return (
    <section
      data-testid="landing-conversation-section"
      className="relative py-24 md:py-32 bg-zinc-950 border-t border-zinc-900"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-xs uppercase tracking-[0.2em] text-[#6C63F5] font-medium mb-5">
              How it feels inside
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.02] tracking-tighter text-zinc-50">
              Network. Collaborate.
              <br />
              <span className="text-zinc-500">Seek advice.</span>
            </h2>
            <p className="mt-6 max-w-md text-zinc-400 text-lg leading-relaxed">
              Explore tags to ideate and discuss with other creatives and work on collaborative projects.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {TAGS.map(({ label, Icon }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-[#6C63F5]/30 bg-[#6C63F5]/10 text-[#c9c5ff] px-3.5 py-1.5 text-xs font-semibold"
                >
                  <Icon size={13} /> {label}
                </span>
              ))}
            </div>

            <button
              onClick={() => navigate('/signup')}
              data-testid="landing-conversation-cta"
              className="landing-btn landing-btn-primary mt-10 group"
            >
              Join the conversation
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </motion.div>
        </div>

        {/* Right stack of chat bubbles */}
        <div className="lg:col-span-7 relative">
          <div className="absolute -inset-8 rounded-[3rem] bg-[#6C63F5]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col gap-5">
            {BUBBLES.map((b, i) => <Bubble key={b.author} b={b} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConversationSection;
